import { Button, Spinner } from "@fluentui/react-components";
import { useMemo } from "react";

import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { EmailCard } from "@/features/documents/EmailCard";
import { EmailDetailDrawer } from "@/features/documents/EmailDetailDrawer";
import { useEmails } from "@/features/documents/useEmails";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { useFilterStore } from "@/store/filterStore";
import { useSelectionStore } from "@/store/selectionStore";

const TEXT_DEBOUNCE_MS = 300;

export function EmailList() {
  // Subscribe to each primitive separately — object-returning selectors
  // break Zustand's reference equality and cause infinite query refetches.
  const subject = useFilterStore((s) => s.subject);
  const from = useFilterStore((s) => s.from);
  const documentType = useFilterStore((s) => s.documentType);
  const listTab = useFilterStore((s) => s.listTab);
  const dateFrom = useFilterStore((s) => s.dateFrom);
  const dateTo = useFilterStore((s) => s.dateTo);
  const sort = useFilterStore((s) => s.sort);
  const tradeKey = useFilterStore((s) => s.tradeKey);
  const correctedOnly = useFilterStore((s) => s.correctedOnly);
  const mode = useFilterStore((s) => s.mode);

  const debouncedSubject = useDebouncedValue(subject, TEXT_DEBOUNCE_MS);
  const debouncedFrom = useDebouncedValue(from, TEXT_DEBOUNCE_MS);
  const debouncedTradeKey = useDebouncedValue(tradeKey, TEXT_DEBOUNCE_MS);

  const filters = useMemo(
    () => ({
      subject: debouncedSubject,
      from: debouncedFrom,
      documentType,
      listTab,
      dateFrom,
      dateTo,
      sort,
      tradeKey: debouncedTradeKey,
      correctedOnly,
      mode,
    }),
    [
      debouncedSubject,
      debouncedFrom,
      documentType,
      listTab,
      dateFrom,
      dateTo,
      sort,
      debouncedTradeKey,
      correctedOnly,
      mode,
    ],
  );
  const query = useEmails(filters);
  const openEmail = useSelectionStore((s) => s.open);

  if (query.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spinner label="Loading emails…" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorState
        title="Failed to load emails"
        message={query.error instanceof Error ? query.error.message : "Unknown error"}
      />
    );
  }

  const emails = query.data?.pages.flatMap((p) => p.emails) ?? [];
  if (emails.length === 0) {
    return <EmptyState message="No emails match these filters." />;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: 16,
        }}
      >
        {emails.map((email) => (
          <EmailCard
            key={email.id}
            email={email}
            onClick={(e) => openEmail(e.id)}
          />
        ))}
        {query.hasNextPage && (
          <div style={{ alignSelf: "center", paddingTop: 8 }}>
            <Button
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
              appearance="secondary"
            >
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </div>
      <EmailDetailDrawer />
    </>
  );
}
